/**
 * 登録編集の枠（03 5.3・3.6.5・3.8・3.15.2）：
 * - FormGrid：PC 2 列・モバイル 1 列のグリッド
 * - FormActions：「キャンセル」「保存」（右下。モバイルは画面の下に固定したバー）。送信中はボタンを押せなくし進みを出す
 * - UnsavedChangesPrompt：未保存の変更があるとき、画面内の移動は MSG-C-015 で確認（useBlocker）、閉じる・再読み込みは beforeunload。
 *   LEAVE_WITHOUT_PROMPT_STATE を付けた移動は確認しない
 */
import { Box, Button, CircularProgress, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { FE_MESSAGES } from '@pcrm/shared';
import { t } from '../../lib/messages';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import { LEGACY_PRIMARY_BUTTON_SX, LEGACY_WHITE_BUTTON_SX } from './legacy-buttons';
import { legacyButtonSx } from '../buttons/legacy-button-sx';

export function FormGrid({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
      {children}
    </Box>
  );
}

/** グリッドの 2 列ぶん（住所・URL など） */
export function FormWide({ children }: { children: ReactNode }) {
  return <Box sx={{ gridColumn: { md: '1 / -1' } }}>{children}</Box>;
}

export function FormActions(props: {
  is_submitting: boolean;
  onCancel: () => void;
  submit_label?: string;
  /** 保存ボタンが <form> の外にあるとき、送る <form> の id（form 属性） */
  form_id?: string;
  /** キャンセルの見た目（262）。既定 'text'。'legacy' は旧PCRM の形（キャンセル＝白地・枠線・黒文字、保存＝オレンジ。どちらも高さ 32px・左右 12px） */
  cancel_variant?: 'text' | 'legacy';
  /** ボタンの形（262 r2）。'legacy' は旧PCRM の .btn（高さ 32px・左右 12px・角 6px・13px）＋旧の色。既定は今の形 */
  button_style?: 'legacy';
  /** 274：ボタンを左に寄せる（旧の 保存→キャンセル の並び。既定は右寄せ） */
  align?: 'start' | 'end';
  /** 274：'submit_first' は 保存 を先（左）に、キャンセル を後に置く（既定は キャンセル→保存） */
  order?: 'cancel_first' | 'submit_first';
  /** 274：ボタンの行の上の余白（px。既定 16px） */
  margin_top?: number;
  /** 274：同じ行の右端に置くもの（旧のロール編集の「削除」） */
  trailing?: ReactNode;
}) {
  const is_legacy_style = props.button_style === 'legacy';
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down('md'));
  const cancel_button = (
    <Button
      variant={props.cancel_variant === 'legacy' ? 'outlined' : 'text'}
      onClick={props.onCancel}
      disabled={props.is_submitting}
      sx={
        is_legacy_style
          ? legacyButtonSx('secondary')
          : // 'legacy'：旧の .btn の形（高さ 32px・左右 12px）＋白地・枠線・黒文字（262 r1）
            props.cancel_variant === 'legacy'
            ? LEGACY_WHITE_BUTTON_SX
            : undefined
      }
    >
      キャンセル
    </Button>
  );
  const submit_button = (
    <Button
      type="submit"
      form={props.form_id}
      variant="contained"
      disabled={props.is_submitting}
      aria-label={props.is_submitting ? FE_MESSAGES['FE-MSG-I-014'].text : undefined}
      // 'legacy' のときは保存ボタンも旧の .btn-primary の形にする（262 r1・r2）
      sx={
        is_legacy_style
          ? legacyButtonSx('primary')
          : props.cancel_variant === 'legacy'
            ? LEGACY_PRIMARY_BUTTON_SX
            : { minWidth: 88 }
      }
    >
      {props.is_submitting ? <CircularProgress size={16} color="inherit" /> : (props.submit_label ?? '保存')}
    </Button>
  );
  const is_submit_first = props.order === 'submit_first';
  const buttons = (
    <>
      {is_submit_first ? submit_button : cancel_button}
      {is_submit_first ? cancel_button : submit_button}
    </>
  );
  const justify = props.align === 'start' ? 'flex-start' : 'flex-end';
  const margin_top = props.margin_top === undefined ? 2 : `${props.margin_top}px`;
  if (is_mobile) {
    return (
      <Paper
        elevation={3}
        sx={{ position: 'sticky', bottom: 0, display: 'flex', justifyContent: justify, gap: 1, p: 1.5, mt: margin_top }}
      >
        {buttons}
        {props.trailing && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            {props.trailing}
          </>
        )}
      </Paper>
    );
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: justify, alignItems: 'center', gap: 1, mt: margin_top }}>
      {buttons}
      {props.trailing && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          {props.trailing}
        </>
      )}
    </Box>
  );
}

/**
 * 確認を出さずに移る navigate の state（保存の後・別の利用者での再ログイン・ログアウト）。
 * 例 navigate(path, { state: LEAVE_WITHOUT_PROMPT_STATE })。ほかの state と一緒に渡すときは展開して混ぜる
 */
export const LEAVE_WITHOUT_PROMPT_STATE = { is_leave_prompt_skipped: true } as const;

function isLeavePromptSkipped(state: unknown): boolean {
  return typeof state === 'object' && state !== null && 'is_leave_prompt_skipped' in state;
}

export function UnsavedChangesPrompt({ is_dirty }: { is_dirty: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      is_dirty && currentLocation.pathname !== nextLocation.pathname && !isLeavePromptSkipped(nextLocation.state),
  );
  // 「破棄して移動」を押したあとの閉じる処理で、移動を取り消さない
  const is_proceeding = useRef(false);

  useEffect(() => {
    if (!is_dirty) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [is_dirty]);

  return (
    <ConfirmDialog
      is_open={blocker.state === 'blocked'}
      message={t('MSG-C-015')}
      confirm_label="破棄して移動"
      onConfirm={() => {
        is_proceeding.current = true;
        blocker.proceed?.();
      }}
      onClose={() => {
        if (!is_proceeding.current && blocker.state === 'blocked') {
          blocker.reset();
        }
        is_proceeding.current = false;
      }}
    />
  );
}
